import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface OrganizationActivity {
  id: string;
  action_type: string;
  action_description: string;
  entity_type?: string;
  entity_id?: string;
  metadata?: any;
  created_at: string;
  user_id?: string;
  user_name?: string;
}

export const useOrganizationActivity = (providerId: string, limit = 10) => {
  const [activities, setActivities] = useState<OrganizationActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!providerId) return;

      try {
        setIsLoading(true);
        setError(null);

        // First get activity logs
        const { data: activityData, error: activityError } = await supabase
          .from('organization_activity_logs')
          .select('*')
          .eq('provider_id', providerId)
          .order('created_at', { ascending: false })
          .limit(limit);

        if (activityError) throw activityError;

        // Get user profiles for activities that have a user_id
        const userIds = (activityData || [])
          .filter(activity => activity.user_id)
          .map(activity => activity.user_id);

        let userProfiles: any[] = [];
        if (userIds.length > 0) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', userIds);
          
          userProfiles = profileData || [];
        }

        // Map activities with user names
        const formattedActivities = (activityData || []).map(activity => {
          const userProfile = userProfiles.find(profile => profile.id === activity.user_id);
          return {
            ...activity,
            user_name: userProfile 
              ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim()
              : activity.user_id ? 'Unknown User' : 'System'
          };
        });

        setActivities(formattedActivities);
      } catch (err) {
        console.error('Error fetching organization activities:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch activities');
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivities();
  }, [providerId, limit]);

  return { activities, isLoading, error };
};